// Lightweight CSV helpers for marketing campaign export/import.
// We hand-roll these to avoid pulling in a CSV dependency for two call sites.

const escapeCell = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const toCSV = (rows, columns) => {
  const header = columns.map((c) => escapeCell(c.label)).join(",");
  const body = rows
    .map((row) =>
      columns
        .map((c) =>
          escapeCell(typeof c.value === "function" ? c.value(row) : row[c.key]),
        )
        .join(","),
    )
    .join("\r\n");
  return body ? `${header}\r\n${body}` : header;
};

export const downloadCSV = (filename, csvContent) => {
  // Prepend UTF-8 BOM so Excel on Windows correctly decodes accented chars.
  const blob = new Blob(["﻿" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Parse a single CSV line, respecting quoted fields and escaped quotes ("").
const parseLine = (line) => {
  const cells = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
};

export const parseCSV = (text) => {
  // Split into logical rows but keep newlines that fall inside quoted cells.
  const rows = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '""';
        i++;
      } else {
        inQuotes = !inQuotes;
        current += ch;
      }
    } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      if (current.length > 0) {
        rows.push(current);
        current = "";
      }
    } else {
      current += ch;
    }
  }
  if (current.length > 0) rows.push(current);
  if (rows.length === 0) return { headers: [], records: [] };

  const headers = parseLine(rows[0]).map((h) => h.trim());
  const records = rows.slice(1).map((line) => {
    const cells = parseLine(line);
    return headers.reduce((acc, header, idx) => {
      acc[header] = (cells[idx] ?? "").trim();
      return acc;
    }, {});
  });
  return { headers, records };
};

// Strip non-digit characters and a leading country code so we can match
// numbers regardless of how they were formatted on export.
export const normalizePhoneDigits = (phone) => {
  if (!phone) return "";
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
};
